import type {AvailableVariable} from './nodeVariables';
import {automationVariableToken} from './variableReferences';

/** Stored as `placeholders`; the UI calls it a variable. */
export interface EmailPlaceholder {
    key: string;
    value: string;
}

export type EmailPlaceholderEdit =
    | {type: 'add'; variable: AvailableVariable}
    | {type: 'select-source'; index: number; variable: AvailableVariable}
    | {type: 'rename'; index: number; key: string}
    | {type: 'remove'; index: number};

export const nextEmailPlaceholderName = (fieldPath: string, placeholders: EmailPlaceholder[]): string => {
    const base = fieldPath.split('.').filter(Boolean).pop()?.replace(/\[\d+\]/g, '') || 'variable';
    const taken = new Set(placeholders.map(placeholder => placeholder.key.trim()));
    let name = base;
    for (let suffix = 1; taken.has(name); suffix += 1) name = `${base}-${suffix}`;
    return name;
};

export const isEmailPlaceholderNameAvailable = (name: string, placeholders: EmailPlaceholder[], index: number): boolean =>
    !!name.trim() && !/[\[\]\r\n]/.test(name)
    && !placeholders.some((placeholder, position) => position !== index && placeholder.key.trim() === name.trim());

// Also applied to the builder's JSON design, not just the exported HTML.
const renameInContent = (value: unknown, from: string, to: string): unknown => {
    if (typeof value === 'string') return value.split(`[[${from}]]`).join(`[[${to}]]`);
    if (Array.isArray(value)) return value.map(entry => renameInContent(entry, from, to));
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, renameInContent(entry, from, to)]));
    }
    return value;
};

/** A rename also rewrites its `[[key]]` uses in subject, content and the builder design. */
export const applyEmailPlaceholderEdit = (config: Record<string, any>, edit: EmailPlaceholderEdit): Record<string, any> => {
    const placeholders: EmailPlaceholder[] = Array.isArray(config.placeholders) ? config.placeholders : [];
    if (edit.type === 'add') {
        return {...config, placeholders: [...placeholders, {
            key: nextEmailPlaceholderName(edit.variable.name, placeholders),
            value: automationVariableToken({nodeName: edit.variable.nodeName ?? '', name: edit.variable.name}),
        }]};
    }
    const current = placeholders[edit.index];
    if (!current) return config;
    if (edit.type === 'remove') {
        return {...config, placeholders: placeholders.filter((_entry, index) => index !== edit.index)};
    }
    if (edit.type === 'select-source') {
        return {...config, placeholders: placeholders.map((entry, index) => index === edit.index ? {
            key: entry.key.trim() ? entry.key : nextEmailPlaceholderName(edit.variable.name, placeholders),
            value: automationVariableToken({nodeName: edit.variable.nodeName ?? '', name: edit.variable.name}),
        } : entry)};
    }
    const name = edit.key.trim();
    if (!isEmailPlaceholderNameAvailable(name, placeholders, edit.index) || current.key === name) return config;
    const next: Record<string, any> = {...config, placeholders: placeholders.map((entry, index) => index === edit.index ? {...entry, key: name} : entry)};
    // An old duplicate still owns the old name; its occurrences must keep pointing to it.
    if (current.key && !placeholders.some((entry, index) => index !== edit.index && entry.key === current.key)) {
        for (const field of ['subject', 'content']) {
            if (config[field] !== undefined) next[field] = renameInContent(config[field], current.key, name);
        }
        if (typeof config.unlayerSourceData === 'string') {
            try {
                const design = JSON.parse(config.unlayerSourceData);
                next.unlayerSourceData = JSON.stringify(renameInContent(design, current.key, name));
            } catch {
                // Preserve an already unreadable design rather than rewriting its raw JSON.
            }
        } else if (config.unlayerSourceData !== undefined) {
            next.unlayerSourceData = renameInContent(config.unlayerSourceData, current.key, name);
        }
    }
    return next;
};
